import { useCallback } from "react";

import { WorkDetailsModel } from "../../../factories/work/model/work-details.model";
import { WorkTaskModel } from "../../../factories/work/model/work-task.model";
import { useAppDispatch, useAppSelector } from "../../../redux/redux";
import { pauseTimer, startTimer } from "../../../services/TimerService";
import {
  getTimerTargetType,
  isTimerForTarget,
  isTimerPaused,
  TimerTarget,
} from "./timerTarget";

type StartWorkTimerArgs = {
  work: WorkDetailsModel;
};

type StartWorkTaskTimerArgs = {
  work: WorkDetailsModel;
  workTask: WorkTaskModel;
};

const getWorkCustomerName = (work: WorkDetailsModel) =>
  work.customer?.customerName || work.customer?.name || "";

export const useTimerTarget = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.appReducer.user);
  const { timer, duration } = useAppSelector((state) => state.timerReducer);

  const startWorkTimer = useCallback(
    ({ work }: StartWorkTimerArgs) => {
      dispatch(
        startTimer({
          workId: work.id,
          customerId: work.customerId,
          assignedUserId: user.id,
          work: {
            id: work.id,
            name: work.name,
          },
          customerName: getWorkCustomerName(work),
        }),
      );
    },
    [dispatch, user.id],
  );

  const startWorkTaskTimer = useCallback(
    ({ work, workTask }: StartWorkTaskTimerArgs) => {
      dispatch(
        startTimer({
          workId: work.id,
          customerId: work.customerId,
          assignedUserId: user.id,
          jobTaskId: workTask.id,
          work: {
            id: work.id,
            name: work.name,
          },
          workTask: {
            id: workTask.id,
            name: workTask.name,
          },
          customerName: getWorkCustomerName(work),
        }),
      );
    },
    [dispatch, user.id],
  );

  const pauseActiveTimer = useCallback(() => {
    if (timer?.id) {
      dispatch(pauseTimer(timer.id));
    }
  }, [dispatch, timer]);

  const resumeActiveTimer = useCallback(() => {
    if (!timer?.id) {
      return;
    }

    dispatch(
      startTimer({
        workId: timer.workId,
        customerId: timer.customerId,
        assignedUserId: timer.assignedUserId,
        jobTaskId:
          getTimerTargetType(timer) === "workTask"
            ? timer.jobTaskId
            : undefined,
        workTask:
          getTimerTargetType(timer) === "workTask" ? timer.workTask : undefined,
        work: timer.work,
        customerName: timer.customerName,
      }),
    );
  }, [dispatch, timer]);

  const toggleActiveTimer = useCallback(() => {
    if (isTimerPaused(timer)) {
      resumeActiveTimer();
      return;
    }

    pauseActiveTimer();
  }, [pauseActiveTimer, resumeActiveTimer, timer]);

  const toggleTimerForTarget = useCallback(
    (target: TimerTarget, start: () => void) => {
      if (isTimerForTarget(timer, target) && !isTimerPaused(timer)) {
        pauseActiveTimer();
        return;
      }

      start();
    },
    [pauseActiveTimer, timer],
  );

  return {
    timer,
    duration,
    startWorkTimer,
    startWorkTaskTimer,
    pauseActiveTimer,
    resumeActiveTimer,
    toggleActiveTimer,
    toggleTimerForTarget,
  };
};
